import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWaitingList, getTodayKST } from '../hooks/useWaitingList';
import { getCreditInfoByStore } from '../api/credit.api';
import {
  callWaiting,
  seatWaiting,
  noShowWaiting,
  cancelWaiting,
  toggleWaiting,
  getStoreSettings,
  updateStoreSettings,
} from '../api/waiting.api';
import type { WaitingAction } from '../components/waiting/WaitingActionButtons';
import Layout from '../components/layout/Layout';
import WaitingCard from '../components/waiting/WaitingCard';
import WaitingSummary from '../components/waiting/WaitingSummary';
import ConfirmModal from '../components/common/ConfirmModal';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { DEMO_WAITINGS } from '../utils/demoData';
import styles from './WaitingListPage.module.css';

type FilterTab = 'all' | 'waiting' | 'called' | 'done';

const TAB_CONFIG: { key: FilterTab; label: string }[] = [
  { key: 'all', label: '전체' },
  { key: 'waiting', label: '대기중' },
  { key: 'called', label: '호출됨' },
  { key: 'done', label: '완료' },
];

export default function WaitingListPage() {
  const { storeId, isDemo } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(getTodayKST());
  const isToday = selectedDate === getTodayKST();

  const realResult = useWaitingList(storeId, selectedDate);
  // 데모 모드: 하드코딩된 4건(매장식사 대기/포장 대기/입장완료/포장완료)으로 대체
  const waitings = isDemo ? DEMO_WAITINGS : realResult.waitings;
  const isLoading = isDemo ? false : realResult.isLoading;
  const error = isDemo ? null : realResult.error;
  const refresh = useMemo(
    () => (isDemo ? () => {} : realResult.refresh),
    [isDemo, realResult.refresh],
  );

  // 대기 접수 상태
  const [isWaitingOpen, setIsWaitingOpen] = useState(true);
  const [isToggling, setIsToggling] = useState(false);

  // 크레딧 잔여
  const navigate = useNavigate();
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  // 알림 설정 상태
  const [sendRegistration, setSendRegistration] = useState(true);
  const [sendPreCall, setSendPreCall] = useState(false);
  const [takeoutEnabled, setTakeoutEnabled] = useState(false);

  // 매장 설정에서 대기 접수 상태 로드
  useEffect(() => {
    if (!storeId) return;
    getStoreSettings(storeId)
      .then((settings) => {
        // isWaitingOpen이 명시적으로 false인 경우만 false
        setIsWaitingOpen(settings.isWaitingOpen !== false);
        setSendRegistration(settings.sendRegistrationNotification !== false);
        setSendPreCall(settings.sendPreCallNotification === true);
        setTakeoutEnabled(settings.takeoutEnabled === true);
      })
      .catch(() => {
        // 실패 시 기본값 유지
      });
    getCreditInfoByStore(storeId)
      .then((info) => setCreditBalance(info.balance))
      .catch(() => {});
  }, [storeId]);

  const handleToggleWaiting = useCallback(async () => {
    if (!storeId || isToggling) return;
    setIsToggling(true);
    try {
      const result = await toggleWaiting(storeId);
      setIsWaitingOpen(result.isWaitingOpen);
    } catch (err) {
      alert(
        err instanceof Error
          ? err.message
          : '대기 상태 변경 중 오류가 발생했습니다.',
      );
    } finally {
      setIsToggling(false);
    }
  }, [storeId, isToggling]);

  const handleToggleRegistration = useCallback(async () => {
    if (!storeId) return;
    const newValue = !sendRegistration;
    setSendRegistration(newValue);
    try {
      await updateStoreSettings(storeId, { sendRegistrationNotification: newValue });
    } catch {
      setSendRegistration(!newValue); // 실패 시 롤백
    }
  }, [storeId, sendRegistration]);

  const handleTogglePreCall = useCallback(async () => {
    if (!storeId) return;
    const newValue = !sendPreCall;
    setSendPreCall(newValue);
    try {
      await updateStoreSettings(storeId, { sendPreCallNotification: newValue });
    } catch {
      setSendPreCall(!newValue); // 실패 시 롤백
    }
  }, [storeId, sendPreCall]);

  const handleToggleTakeout = useCallback(async () => {
    if (!storeId) return;
    const newValue = !takeoutEnabled;
    setTakeoutEnabled(newValue);
    try {
      await updateStoreSettings(storeId, { takeoutEnabled: newValue });
    } catch {
      setTakeoutEnabled(!newValue);
    }
  }, [storeId, takeoutEnabled]);

  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: (() => Promise<void>) | null;
  }>({
    isOpen: false,
    title: '',
    message: '',
    action: null,
  });

  const filteredWaitings = useMemo(() => {
    switch (activeTab) {
      case 'waiting':
        return waitings.filter((w) => w.status === 'WAITING');
      case 'called':
        return waitings.filter((w) => w.status === 'CALLED');
      case 'done':
        return waitings.filter(
          (w) =>
            w.status === 'SEATED' ||
            w.status === 'NO_SHOW' ||
            w.status === 'CANCELLED',
        );
      default:
        return waitings;
    }
  }, [waitings, activeTab]);

  const handleAction = useCallback(
    async (waitingId: string, action: WaitingAction) => {
      if (!isToday) return; // 과거 데이터에서는 액션 차단
      if (isDemo) {
        alert('데모 모드에서는 액션이 적용되지 않습니다.');
        return;
      }

      const executeAction = async () => {
        try {
          switch (action) {
            case 'call':
              await callWaiting(waitingId);
              break;
            case 'seat':
              await seatWaiting(waitingId);
              break;
            case 'noShow':
              await noShowWaiting(waitingId);
              break;
            case 'cancel':
              await cancelWaiting(waitingId);
              break;
          }
          refresh();
        } catch (err) {
          alert(
            err instanceof Error
              ? err.message
              : '처리 중 오류가 발생했습니다.',
          );
        }
      };

      // 노쇼, 취소는 확인 모달 표시
      if (action === 'noShow') {
        setConfirmModal({
          isOpen: true,
          title: '노쇼 처리',
          message: '해당 고객을 노쇼 처리하시겠습니까?',
          action: executeAction,
        });
        return;
      }

      if (action === 'cancel') {
        setConfirmModal({
          isOpen: true,
          title: '대기 취소',
          message: '해당 고객의 대기를 취소하시겠습니까?',
          action: executeAction,
        });
        return;
      }

      await executeAction();
    },
    [refresh, isToday, isDemo],
  );

  const handleConfirm = async () => {
    if (confirmModal.action) {
      await confirmModal.action();
    }
    setConfirmModal({ isOpen: false, title: '', message: '', action: null });
  };

  const handleCancelModal = () => {
    setConfirmModal({ isOpen: false, title: '', message: '', action: null });
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setActiveTab('all');
  };

  const handleGoToToday = () => {
    setSelectedDate(getTodayKST());
    setActiveTab('all');
  };

  /** 선택된 날짜를 한국어로 표시 (예: 2026년 4월 12일 토요일) */
  const formattedDate = useMemo(() => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];
    return `${y}년 ${m}월 ${d}일 ${dayNames[date.getDay()]}요일`;
  }, [selectedDate]);

  return (
    <Layout title="대기관리">
      <div className={styles.page}>
        {/* 날짜 선택기 */}
        <div className={styles.datePickerArea}>
          <div className={styles.datePickerRow}>
            <label className={styles.dateLabel} htmlFor="waiting-date">
              날짜 선택
            </label>
            <input
              id="waiting-date"
              type="date"
              className={styles.dateInput}
              value={selectedDate}
              max={getTodayKST()}
              onChange={handleDateChange}
            />
            {!isToday && (
              <button
                type="button"
                className={styles.todayButton}
                onClick={handleGoToToday}
              >
                오늘
              </button>
            )}
          </div>
          <div className={styles.dateDisplay}>{formattedDate}</div>
          {!isToday && (
            <div className={styles.pastBanner}>
              과거 기록 조회 중 &mdash; 호출/입장/노쇼 등의 액션은 비활성화됩니다.
            </div>
          )}
        </div>

        {/* 잔여 크레딧 */}
        <div className={styles.creditBar}>
          <span className={styles.creditLabel}>잔여 메시지</span>
          <span className={`${styles.creditValue} ${creditBalance !== null && creditBalance < 100 ? styles.creditLow : ''}`}>
            {creditBalance !== null ? creditBalance.toLocaleString() : '-'}건
          </span>
          <button
            type="button"
            className={styles.creditChargeBtn}
            onClick={() => navigate('/credits')}
          >
            충전
          </button>
        </div>

        {/* 알림 설정 토글 */}
        {isToday && (
          <div className={styles.notificationToggles}>
            <div className={styles.toggleRow}>
              <div className={styles.toggleInfo}>
                <span className={styles.toggleLabel}>웨이팅 접수 완료 메시지</span>
                <span className={styles.toggleDesc}>대기 등록 시 고객에게 확인 메시지 발송</span>
              </div>
              <button
                type="button"
                className={`${styles.toggleSwitch} ${sendRegistration ? styles.toggleOn : styles.toggleOff}`}
                onClick={handleToggleRegistration}
                aria-label="웨이팅 접수 완료 메시지 토글"
              >
                <span className={styles.toggleKnob} />
              </button>
            </div>
            <div className={styles.toggleRow}>
              <div className={styles.toggleInfo}>
                <span className={styles.toggleLabel}>입장 임박 알림 메시지</span>
                <span className={styles.toggleDesc}>순서가 가까워지면 사전 알림 발송</span>
              </div>
              <button
                type="button"
                className={`${styles.toggleSwitch} ${sendPreCall ? styles.toggleOn : styles.toggleOff}`}
                onClick={handleTogglePreCall}
                aria-label="입장 임박 알림 메시지 토글"
              >
                <span className={styles.toggleKnob} />
              </button>
            </div>
            <div className={styles.toggleRow}>
              <div className={styles.toggleInfo}>
                <span className={styles.toggleLabel}>포장 주문</span>
                <span className={styles.toggleDesc}>키오스크에서 포장/매장식사 선택 가능</span>
              </div>
              <button
                type="button"
                className={`${styles.toggleSwitch} ${takeoutEnabled ? styles.toggleOn : styles.toggleOff}`}
                onClick={handleToggleTakeout}
                aria-label="포장 주문 토글"
              >
                <span className={styles.toggleKnob} />
              </button>
            </div>
          </div>
        )}

        {/* 대기 마감 토글 */}
        {isToday && (
          <div className={styles.waitingToggleArea}>
            {!isWaitingOpen && (
              <div className={styles.waitingClosedBanner}>
                대기 마감 중
              </div>
            )}
            <button
              type="button"
              className={
                isWaitingOpen
                  ? styles.waitingCloseButton
                  : styles.waitingOpenButton
              }
              onClick={handleToggleWaiting}
              disabled={isToggling}
            >
              {isToggling
                ? '처리 중...'
                : isWaitingOpen
                  ? '대기 마감'
                  : '대기 접수 시작'}
            </button>
          </div>
        )}

        {error && (
          <div className={styles.errorBar}>
            <span>{error}</span>
            <button
              type="button"
              className={styles.retryButton}
              onClick={refresh}
            >
              재시도
            </button>
          </div>
        )}

        <div className={styles.summaryArea}>
          <WaitingSummary waitings={waitings} />
        </div>

        <div className={styles.tabs}>
          {TAB_CONFIG.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`${styles.tab} ${
                activeTab === tab.key ? styles.tabActive : ''
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading && waitings.length === 0 ? (
          <LoadingSpinner />
        ) : filteredWaitings.length === 0 ? (
          <div className={styles.empty}>
            <div>
              {isToday
                ? '현재 대기 중인 팀이 없습니다'
                : '해당 날짜의 대기 기록이 없습니다'}
            </div>
          </div>
        ) : (
          <div className={`${styles.list} ${!isToday ? styles.listPast : ''}`}>
            {filteredWaitings.map((waiting) => (
              <WaitingCard
                key={waiting.id}
                waiting={waiting}
                onAction={handleAction}
                disabled={!isToday}
              />
            ))}
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={handleConfirm}
        onCancel={handleCancelModal}
      />
    </Layout>
  );
}
